import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import type { Props as NativeIssueTableProps } from '@atlassian/jira-native-issue-table/src/ui/types.tsx';
import type {
	useColumnResetMutation as UseColumnResetMutation,
	useColumnResetMutation$data as UseColumnResetMutationData,
} from '@atlassian/jira-relay/src/__generated__/useColumnResetMutation.graphql';
import { onColumnsChangeErrorFlag } from '../../common/flags/index.tsx';

interface Props {
	fieldSetViewId: string | null;
	onSuccess?: (success: boolean | undefined) => void;
	onError?: (error: Error) => void;
}

export const useColumnReset = ({ fieldSetViewId, onSuccess, onError }: Props) => {
	const { showFlag } = useFlagsService();

	const [replaceListViewFieldSets] = useMutation<UseColumnResetMutation>(graphql`
		mutation useColumnResetMutation($id: ID!, $fieldSetsInput: JiraFieldSetsMutationInput)
		@raw_response_type {
			jira {
				updateFieldSetsView(id: $id, fieldSetsInput: $fieldSetsInput) {
					success
					view {
						id
					}
				}
			}
		}
	`);

	/**
	 * When the the mutation is unsuccessful then return an error flag and fire off error analytics
	 * otherwise modify the columns with the new fieldset
	 */
	const replaceIssueSearchViewFieldSetOnComplete = useCallback(
		(success: boolean | undefined) => {
			onSuccess?.(success);
			if (!success) {
				showFlag(onColumnsChangeErrorFlag);
				fireErrorAnalytics({
					meta: {
						id: 'commitReplaceJiraIssueCommonViewsFieldSetsMutationUnsuccessful',
						packageName: 'jiraIssueViewCommonViews',
						teamName: 'bento',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[onSuccess, showFlag],
	);

	/**
	 * When the the mutation is unsuccessful then return an error flag and fire off error analytics
	 */
	const replaceIssueSearchViewFieldSetOnError = useCallback(
		(error: Error) => {
			onError?.(error);
			showFlag(onColumnsChangeErrorFlag);
			fireErrorAnalytics({
				meta: {
					id: 'commitReplaceJiraIssueCommonViewsSetsMutationError',
					packageName: 'jiraIssueViewCommonViews',
					teamName: 'bento',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		[onError, showFlag],
	);
	/**
	 * onColumnsChange is invoked when there is a change in the list of visible columns.
	 * For optimistic updates the optimisticUpdater gets called
	 * and we see the expected change then mutation happens, optimisticUpdater gets
	 * reverted as a result of mutation then updater gets called to persist the optimistic update view.
	 */
	const onColumnsReset = useCallback<
		NonNullable<NativeIssueTableProps['onRestoreDefaultColumns']>
	>(() => {
		replaceListViewFieldSets({
			variables: {
				id: fieldSetViewId || '',
				fieldSetsInput: { resetToDefaultFieldSets: true },
			},
			onCompleted(columnData: UseColumnResetMutationData) {
				replaceIssueSearchViewFieldSetOnComplete(columnData.jira?.updateFieldSetsView?.success);
			},
			onError(error: Error) {
				replaceIssueSearchViewFieldSetOnError(error);
			},
		});
	}, [
		replaceIssueSearchViewFieldSetOnComplete,
		replaceIssueSearchViewFieldSetOnError,
		replaceListViewFieldSets,
		fieldSetViewId,
	]);

	return onColumnsReset;
};
